import { createClient } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { withRetry } from './api-utils';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Missing Supabase environment variables. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your .env file.'
  );
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      // Use a no-op lock to avoid AbortError issues with navigator.locks
      lock: async <R,>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
        return await fn();
      },
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'koda-auth',
    },
  }
);

// Auth helpers
export const signInWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signUpWithEmail = async (email: string, password: string, name: string, tosAccepted?: boolean) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        name,
        ...(tosAccepted && {
          tos_accepted_at: new Date().toISOString(),
          tos_version: '1.0',
        }),
      },
    },
  });
  return { data, error };
};

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  return { user, error };
};

// File upload helper
export const uploadFile = async (
  bucket: string,
  path: string,
  file: File
) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });
  return { data, error };
};

export const getFileUrl = async (bucket: string, path: string) => {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
};

// Password management
export const updatePassword = async (newPassword: string) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  return { data, error };
};

export const resetPasswordForEmail = async (email: string) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
  });
  return { data, error };
};

// Two-Factor Authentication (MFA/TOTP)
export const enrollMFA = async () => {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'Authenticator App',
  });
  return { data, error };
};

export const verifyMFA = async (factorId: string, challengeId: string, code: string) => {
  const { data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId,
    code,
  });
  return { data, error };
};

export const challengeMFA = async (factorId: string) => {
  const { data, error } = await supabase.auth.mfa.challenge({
    factorId,
  });
  return { data, error };
};

export const unenrollMFA = async (factorId: string) => {
  const { data, error } = await supabase.auth.mfa.unenroll({
    factorId,
  });
  return { data, error };
};

export const getMFAFactors = async () => {
  const { data, error } = await supabase.auth.mfa.listFactors();
  return { data, error };
};

// Get user preferences
export const getUserPreferences = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_preferences')
    .select('*')
    .eq('user_id', userId)
    .single();
  return { data, error };
};

// Update user preferences
export const updateUserPreferences = async (
  userId: string,
  preferences: {
    study_reminders?: boolean;
    weekly_progress?: boolean;
    product_updates?: boolean;
    email_notifications?: boolean;
  }
) => {
  const { data, error } = await supabase
    .from('user_preferences')
    .upsert({
      user_id: userId,
      ...preferences,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();
  return { data, error };
};

// Helper to report Supabase errors to Sentry
export const reportSupabaseError = (
  error: { message: string; code?: string; details?: string; hint?: string } | null,
  context: string
) => {
  if (error) {
    Sentry.captureException(new Error(`Supabase Error: ${error.message}`), {
      tags: {
        source: 'supabase',
        context,
        code: error.code,
      },
      extra: {
        details: error.details,
        hint: error.hint,
      },
    });
  }
};

// Type for Supabase query results
type SupabaseResult<T> = { data: T | null; error: { message: string; code?: string; details?: string; hint?: string } | null };

// Wrapper that automatically reports errors to Sentry
export async function withErrorReporting<T>(
  context: string,
  query: Promise<SupabaseResult<T>>
): Promise<SupabaseResult<T>> {
  const result = await query;
  if (result.error) {
    reportSupabaseError(result.error, context);
  }
  return result;
}

// Invoke an edge function with retry on transient failures
export async function invokeWithRetry<T = unknown>(
  functionName: string,
  body: object
): Promise<{ data: T | null; error: { message: string } | null }> {
  return Sentry.startSpan(
    { name: `edge.${functionName}`, op: 'http.client', attributes: { 'edge_function.name': functionName } },
    () => withRetry(
      async () => {
        const { data, error } = await supabase.functions.invoke(functionName, {
          body,
        });
        if (error) {
          throw new Error(error.message || `Edge function ${functionName} error`);
        }
        return { data: data as T, error: null };
      },
      {
        maxRetries: 2,
        shouldRetry: (err) => {
          if (err instanceof Error) {
            const msg = err.message.toLowerCase();
            return msg.includes('network') || msg.includes('failed to fetch') ||
              msg.includes('500') || msg.includes('502') || msg.includes('503') ||
              msg.includes('504') || msg.includes('timed out');
          }
          return false;
        },
      }
    ).catch((error) => ({
      data: null,
      error: { message: error instanceof Error ? error.message : String(error) },
    }))
  );
}
